/**
 * Geometry Edit Helpers
 *
 * Centralized utility functions for geometry editing operations.
 * Implements SSOT (Single Source of Truth) pattern to eliminate code duplication.
 *
 * @module utils/geometryEditHelpers
 */

import type { Task, TaskGeometry, Coordinates } from '../types';
import { TaskFormMode } from '../types';
import { useGeoMarkupStore } from '../store';
import {
  selectIsGeometryEditingActive,
  selectGeometryEditingMode,
} from '../store/selectors';

/**
 * Deep equality comparison for Coordinates
 */
const areCoordinatesEqual = (
  a: Coordinates | undefined,
  b: Coordinates | undefined
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.lat === b.lat && a.lng === b.lng;
};

/**
 * Deep equality comparison for array of Coordinates
 */
const areCoordinateArraysEqual = (
  a: Coordinates[] | undefined,
  b: Coordinates[] | undefined
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((coord, index) => areCoordinatesEqual(coord, b[index]));
};

/**
 * Deep equality comparison for two TaskGeometry objects
 *
 * Compares all properties of TaskGeometry including nested objects.
 * Used for detecting unsaved geometry changes in the task form.
 *
 * @param a - First geometry to compare
 * @param b - Second geometry to compare
 * @returns true if geometries are equal, false otherwise
 */
const areGeometriesEqual = (a: TaskGeometry, b: TaskGeometry): boolean => {
  // Basic properties
  if (a.id !== b.id) return false;
  if (a.type !== b.type) return false;
  if (a.name !== b.name) return false;
  if (a.description !== b.description) return false;
  if (a.isPlaceholder !== b.isPlaceholder) return false;

  // Calculated center
  if (!areCoordinatesEqual(a.calculatedCenter, b.calculatedCenter))
    return false;

  // Marker properties
  if (a.markerShape !== b.markerShape) return false;
  if (a.markerColor !== b.markerColor) return false;
  if (a.markerIcon !== b.markerIcon) return false;

  // Line properties
  if (!areCoordinateArraysEqual(a.linePoints, b.linePoints)) return false;
  if (a.lineType !== b.lineType) return false;
  if (a.lineStyle !== b.lineStyle) return false;
  if (a.lineWidth !== b.lineWidth) return false;
  if (a.lineColor !== b.lineColor) return false;

  // Shape properties
  if (a.shapeType !== b.shapeType) return false;
  if (!areCoordinateArraysEqual(a.shapePoints, b.shapePoints)) return false;
  if (a.shapeFill !== b.shapeFill) return false;
  if (a.shapeFillColor !== b.shapeFillColor) return false;
  if (a.shapeBorderStyle !== b.shapeBorderStyle) return false;
  if (a.shapeBorderWidth !== b.shapeBorderWidth) return false;
  if (a.shapeBorderColor !== b.shapeBorderColor) return false;

  // Center marker (nested object)
  if (a.centerMarker !== b.centerMarker) {
    // Both undefined/null
    if (!a.centerMarker && !b.centerMarker) return true;
    // One is undefined/null
    if (!a.centerMarker || !b.centerMarker) return false;
    // Both exist - compare properties
    if (a.centerMarker.visible !== b.centerMarker.visible) return false;
    if (a.centerMarker.shape !== b.centerMarker.shape) return false;
    if (a.centerMarker.color !== b.centerMarker.color) return false;
    if (a.centerMarker.icon !== b.centerMarker.icon) return false;
  }

  return true;
};

/**
 * Deep equality comparison for arrays of TaskGeometry
 *
 * Compares two geometry arrays for equality. Used to detect unsaved changes
 * in the task form when user modifies geometries.
 *
 * **Performance:** O(n²) in worst case, but optimized with early exits.
 * For typical task geometry counts (1-10), performance is acceptable.
 *
 * @param a - First geometry array
 * @param b - Second geometry array
 * @returns true if arrays are equal, false otherwise
 *
 * @example
 * ```typescript
 * const hasChanges = !areGeometryArraysEqual(currentGeometries, editingTask.geometries);
 * ```
 */
export const areGeometryArraysEqual = (
  a: TaskGeometry[] | undefined,
  b: TaskGeometry[] | undefined
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;

  // Compare each geometry (order matters for user experience)
  return a.every((geomA, index) => {
    const geomB = b[index];
    return geomB && areGeometriesEqual(geomA, geomB);
  });
};

/**
 * Opens task for editing with specific geometry activated
 *
 * SSOT for all geometry edit operations (double-click, context menu, long-press, etc.)
 * Used by: TaskMarker, PreviewableMarker, useGeometryInteraction
 *
 * **Guard behavior:**
 * - **CREATE mode guard:** If form is open in CREATE mode (taskId="preview"),
 *   ONLY activates the geometry. Prevents openTaskForEdit call which would
 *   switch mode to 'edit' and reset the form.
 * - **EDIT mode guard:** If already editing the same task, ONLY activates
 *   the geometry without reloading geometries or reopening the form.
 *   Prevents unnecessary initialization (GPS detection, etc.).
 *
 * **Implementation:**
 * Uses `useGeoMarkupStore.getState()` to get CURRENT state directly from store,
 * ensuring guard always uses fresh values (not stale closure values).
 *
 * **Critical sequence (NEW task or different task):**
 * 1. Load geometries FIRST (ensures geometry exists in store)
 * 2. Activate geometry (also sets activeTool automatically)
 * 3. Open form LAST (triggers UI updates)
 *
 * @param taskId - ID of task to edit
 * @param geometryId - ID of geometry to activate
 * @param coordinates - Coordinates for zooming (used only when opening new task)
 * @param dependencies - Zustand actions and data
 *
 * @example
 * ```typescript
 * // In double-click handler:
 * openTaskForEditWithGeometry(task.id, geometry.id, coordinates, {
 *   allTasks,
 *   loadGeometries,
 *   setActiveGeometry,
 *   openTaskForEdit,
 * });
 * ```
 */
export const openTaskForEditWithGeometry = (
  taskId: string,
  geometryId: string,
  coordinates: Coordinates,
  {
    allTasks,
    loadGeometries,
    setActiveGeometry,
    openTaskForEdit,
  }: {
    allTasks: Task[];
    loadGeometries: (geometries: TaskGeometry[]) => void;
    setActiveGeometry: (id: string | null) => void;
    openTaskForEdit: (taskId: string, coords: Coordinates) => void;
  }
): void => {
  // Get CURRENT state directly from store (not from closure - always fresh!)
  const currentState = useGeoMarkupStore.getState();

  // ===== RESOLVE SPECIAL 'preview' TASK ID =====
  // CurrentGeometriesRenderer uses taskId="preview" as placeholder during create/edit
  // When user double-clicks geometry in EDIT mode, we need to resolve to actual task being edited
  let resolvedTaskId = taskId;
  if (taskId === 'preview' && currentState.tasks.selectedId) {
    resolvedTaskId = currentState.tasks.selectedId;
  }

  // ===== GUARD #1: Check if already in CREATE mode =====
  // When creating a new task (taskId="preview", selectedId=null), double-clicking a geometry
  // should ONLY activate it for editing, without triggering openTaskForEdit (which would
  // switch mode from 'create' to 'edit' and reset the entire form with all user data).
  const isGeometryEditingActive = selectIsGeometryEditingActive(currentState);
  const geometryEditingMode = selectGeometryEditingMode(currentState);

  const isAlreadyInCreateMode =
    isGeometryEditingActive &&
    geometryEditingMode === TaskFormMode.Create &&
    taskId === 'preview';

  if (isAlreadyInCreateMode) {
    // Already creating task - ONLY activate geometry
    // Don't call openTaskForEdit (would switch to edit mode and reset form)
    setActiveGeometry(geometryId);
    return; // ⚡ Early exit - no loadGeometries, no openTaskForEdit
  }

  // ===== GUARD #2: Check if already editing this exact task =====
  const isAlreadyEditingSameTask =
    isGeometryEditingActive &&
    geometryEditingMode === TaskFormMode.Edit &&
    currentState.tasks.selectedId === resolvedTaskId;

  if (isAlreadyEditingSameTask) {
    // Already editing this task - ONLY activate geometry
    // Geometries are already loaded in store, so no need to reload
    // This prevents triggering useToolbarInitialization and GPS detection
    setActiveGeometry(geometryId);
    return; // ⚡ Early exit - no loadGeometries, no openTaskForEdit
  }

  // ===== NORMAL FLOW: Full initialization for new/different task =====
  // 1. Load geometries BEFORE opening form (critical for edit mode to work!)
  // This ensures that when setActiveGeometry is called, the geometry exists in store
  const currentTask = allTasks.find((t: Task) => t.id === resolvedTaskId);
  if (currentTask?.geometries) {
    loadGeometries(currentTask.geometries);
  }

  // 2. Activate geometry
  // Note: Updated setActiveGeometry automatically sets activeTool based on geometry type
  // This eliminates the need to call setActiveTool separately and prevents placeholder creation
  setActiveGeometry(geometryId);

  // 3. Open edit form (must be LAST to ensure geometries are loaded first)
  // This triggers UI updates, zooming, and cache clearing
  // Use shortId for URL-friendly navigation; fall back to GUID if not found
  const taskShortId = currentTask?.shortId ?? resolvedTaskId;
  openTaskForEdit(taskShortId, coordinates);
};

/**
 * Opens task for editing with simplified inline implementation
 *
 * Lightweight helper for cases where you just need basic edit form opening
 * without the full geometry activation logic of openTaskForEditWithGeometry.
 *
 * **Guard behavior:**
 * If already editing the same task, ONLY zooms to coordinates without
 * reopening the form.
 *
 * **Implementation:**
 * Uses `useGeoMarkupStore.getState()` to get CURRENT state directly from store,
 * ensuring guard always uses fresh values (not stale closure values).
 *
 * @param taskId - ID of task to edit
 * @param coordinates - Coordinates for zooming
 *
 * @example
 * ```typescript
 * // In simple double-click handler:
 * openTaskForEditInline(task.id, coordinates);
 * ```
 */
export const openTaskForEditInline = (
  taskId: string,
  coordinates: Coordinates
): void => {
  const state = useGeoMarkupStore.getState();
  const isGeometryEditingActive = selectIsGeometryEditingActive(state);
  const geometryEditingMode = selectGeometryEditingMode(state);

  const isAlreadyEditingSameTask =
    isGeometryEditingActive &&
    geometryEditingMode === TaskFormMode.Edit &&
    state.tasks.selectedId === taskId;

  if (isAlreadyEditingSameTask) {
    state.map.zoomTo(coordinates);
    return;
  }

  state.tasks.selectTask(taskId);
  state.map.zoomTo(coordinates);
  state.tasks.openForm('edit', taskId);
};
